/**
 * /admin/llm-usage — LLM cost rollup by day × model × purpose.
 *
 * Two visualisations:
 *   - top: totals across the selected window (calls / tokens / $)
 *   - main: bar-per-day (CSS bars from a `<div>` width, no chart lib),
 *     stacked by model
 *   - bottom: drill-down table (day / model / purpose / calls / tokens / $)
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type LlmUsageResponse, Routes } from '@tg-leads/contracts';

import { api } from '../../lib/api';

export function LlmUsagePage() {
  const [days, setDays] = useState(14);

  const q = useQuery({
    queryKey: ['llmUsage', days],
    queryFn: () =>
      api.get<LlmUsageResponse>(`${Routes.admin.llmUsage}?days=${days}`),
  });

  const perDay = useMemo(() => {
    const map = new Map<string, { cost: number; calls: number }>();
    (q.data?.rows ?? []).forEach((r) => {
      const cur = map.get(r.day) ?? { cost: 0, calls: 0 };
      cur.cost += r.costUsd;
      cur.calls += r.calls;
      map.set(r.day, cur);
    });
    return Array.from(map.entries())
      .map(([day, v]) => ({ day, ...v }))
      .sort((a, b) => a.day.localeCompare(b.day));
  }, [q.data]);

  const maxCost = Math.max(0.0001, ...perDay.map((d) => d.cost));

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">模型调用用量</h1>
          <p className="text-sm text-slate-500 mt-1">
            按"日期 × 模型 × 用途"聚合。Mock 测试也会按价目表算费用（实际花费 $0），便于联调全链路。
          </p>
        </div>
        <div>
          <label className="label">统计窗口（天）</label>
          <select
            className="input"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={1}>1</option>
            <option value={7}>7</option>
            <option value={14}>14</option>
            <option value={30}>30</option>
            <option value={90}>90</option>
          </select>
        </div>
      </div>

      {q.isLoading ? (
        <div className="text-slate-500 text-sm">加载中…</div>
      ) : !q.data ? (
        <div className="text-red-600 text-sm">加载失败</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Tile label={`近 ${days} 天调用次数`} value={q.data.totals.calls} />
            <Tile
              label={`近 ${days} 天 token 总数`}
              value={q.data.totals.totalTokens.toLocaleString()}
            />
            <Tile
              label={`近 ${days} 天费用（美元）`}
              value={`$${q.data.totals.costUsd.toFixed(4)}`}
            />
          </div>

          <div className="card">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">
              每日费用（横条长度 = 当日美元）
            </h2>
            {perDay.length === 0 ? (
              <div className="text-xs text-slate-500">该窗口无数据</div>
            ) : (
              <ul className="space-y-1.5 text-xs">
                {perDay.map((d) => (
                  <li key={d.day} className="flex items-center gap-3">
                    <span className="font-mono w-24 text-slate-500">
                      {d.day}
                    </span>
                    <div className="flex-1 bg-slate-100 rounded h-5 relative overflow-hidden">
                      <div
                        className="absolute top-0 left-0 h-full bg-indigo-500/80"
                        style={{
                          width: `${(d.cost / maxCost) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="font-mono w-20 text-right">
                      ${d.cost.toFixed(4)}
                    </span>
                    <span className="font-mono w-14 text-right text-slate-500">
                      {d.calls}×
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="card overflow-x-auto">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">
              明细（日期 × 模型 × 用途）
            </h2>
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500 uppercase border-b">
                  <th className="py-2 pr-4">日期</th>
                  <th className="py-2 pr-4">模型</th>
                  <th className="py-2 pr-4">用途</th>
                  <th className="py-2 pr-4 text-right">调用次数</th>
                  <th className="py-2 pr-4 text-right">输入 token</th>
                  <th className="py-2 pr-4 text-right">输出 token</th>
                  <th className="py-2 pr-4 text-right">费用（美元）</th>
                </tr>
              </thead>
              <tbody>
                {q.data.rows.map((r, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-mono">{r.day}</td>
                    <td className="py-2 pr-4">{r.model}</td>
                    <td className="py-2 pr-4">{r.purpose}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {r.calls}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {r.inputTokens}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {r.outputTokens}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      ${r.costUsd.toFixed(6)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="card border-l-4 border-indigo-400 px-4 py-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-semibold mt-1 tabular-nums">{value}</div>
    </div>
  );
}
